/* eslint-disable no-unused-vars */
import React from 'react';
import { styled } from '@linaria/react';
import Matter from 'matter-js';

import usePhysicsEngine from './use-physics-engine';
import useInterval from '@/hooks/use-interval';
import useWindowDimensions from '@/hooks/use-window-dimensions';
import {
  sample,
  random,
  normalize,
  throttle,
  convertDegreesToRadians,
} from '@/utils';

import DEFAULT_SPRITES from './default-sprites';

interface Props {
  position: [number, number]; // [top, left]
  enableCollisions: boolean;
  airFriction: number;
  velocity: number;
  // Affects rotation speed
  angularVelocity: number;
  // The direction that the geyser should be shooting particles:
  angle: number;
  // The amount of deviation from the specified angle, in degrees:
  spread: number;
  // The amount of deviation from the specified velocity:
  volatility: number;
  // The rate of particles fired, specified as # per second:
  concentration: number;
  // How long to emit confetti for. Set to `Infinity` to never stop:
  duration?: number;
}

interface GeneratedParticlesParams extends Props {
  engine: Matter.Engine | null;
  duration: number;
}

const useGeneratedParticles = ({
  engine,
  position,
  angle,
  velocity,
  concentration,
  duration,
  airFriction,
  angularVelocity,
  enableCollisions,
  volatility,
  spread,
}: GeneratedParticlesParams) => {
  const timeBetweenParticles =
    concentration === 0 ? null : 1000 / concentration;

  const startAt = React.useMemo(() => Date.now(), []);

  useInterval(() => {
    if (!engine) {
      return;
    }

    if (Date.now() > startAt + duration) {
      return;
    }

    const [top, left] = position;

    const [sprite] = sample(DEFAULT_SPRITES);

    const config: Matter.IChamferableBodyDefinition = {
      frictionAir: airFriction * sprite.airFrictionMultiplier,
      render: {
        sprite: {
          xScale: 1,
          yScale: 1,
          texture: sprite.src,
        },
      },
    };

    if (!enableCollisions) {
      config.collisionFilter = {
        category: undefined,
      };
    }

    const particle = Matter.Bodies.rectangle(
      top,
      left,
      sprite.width,
      sprite.height,
      config
    );

    const particleAngle = random(angle - spread, angle + spread);

    const velocityMultiple = Math.random();

    // `normalize` maps a value from one range to another.
    // Our `velocityMultiple` is a random number between 0 and 1, and we want
    // to map that value to the current range of our velocity/angularVelocity,
    // based on volatility.
    //
    // eg. if velocity is 50 and volatility is 0.5, our velocity range is
    // 25-75. This does the following mapping for `velocityMultiple`:
    //
    //   0    -> 25
    //   0.5  -> 50
    //   0.75 -> 62.5
    //   1    -> 75
    const particleVelocity = normalize(
      velocityMultiple,
      0,
      1,
      velocity - velocity * volatility,
      velocity + velocity * volatility
    );
    const particleAngularVelocity = normalize(
      velocityMultiple,
      0,
      1,
      angularVelocity - angularVelocity * volatility,
      angularVelocity + angularVelocity * volatility
    );

    const particleAngleInRads =
      convertDegreesToRadians(particleAngle);

    const x = Math.cos(particleAngleInRads) * particleVelocity;
    const y = Math.sin(particleAngleInRads) * particleVelocity;

    Matter.Body.setVelocity(particle, { x, y });
    Matter.Body.setAngularVelocity(particle, particleAngularVelocity);

    Matter.World.add(engine.world, [particle]);
  }, timeBetweenParticles);
};

const useParticleCleanup = (engine: Matter.Engine | null) => {
  useInterval(() => {
    if (!engine) {
      return;
    }

    Matter.Composite.allBodies(engine.world).forEach((particle) => {
      if (particle.position.y > window.innerHeight) {
        Matter.World.remove(engine.world, particle);
      }
    });
  }, 500);
};

const useMouseWind = (engine: Matter.Engine | null) => {
  React.useEffect(() => {
    if (!engine) {
      return;
    }

    type MousePosition = [number, number];

    let lastMousePosition: MousePosition | null = null;
    let lastMoveAt: number | null = null;

    const handleMouseMove = throttle((event: MouseEvent) => {
      const { clientX, clientY } = event;

      const newLastMousePosition: MousePosition = [clientX, clientY];
      const newLastMoveAt = Date.now();

      if (!lastMoveAt || !lastMousePosition) {
        lastMoveAt = newLastMoveAt;
        lastMousePosition = newLastMousePosition;

        return;
      }

      const deltaX = newLastMousePosition[0] - lastMousePosition[0];
      const deltaY = newLastMousePosition[1] - lastMousePosition[1];
      const deltaTime = newLastMoveAt - lastMoveAt;

      const xPerSecond = deltaX / deltaTime;
      const yPerSecond = deltaY / deltaTime;

      const multiplier = 0.5;

      let maxXEffect = xPerSecond * multiplier;
      let maxYEffect = yPerSecond * multiplier;

      lastMousePosition = newLastMousePosition;
      lastMoveAt = newLastMoveAt;

      Matter.Composite.allBodies(engine.world).forEach((particle) => {
        // We now have a Δx and a Δy for how far the particle is moving per
        // second in each direction, and we have to convert that into a
        // force we can apply onto each particle.
        const aSquared = (clientX - particle.position.x) ** 2;
        const bSquared = (clientY - particle.position.y) ** 2;
        const distanceToMouse = Math.sqrt(aSquared + bSquared);

        // If the mouse is more than `threshold` away, don't pay attention to it. This is partially a performance thing (to avoid applying a force to an item with a negligible effect), but also to help make sure that wind feels properly localized. It shouldn't affect things more than `threshold` pixels away.
        const threshold = 300;
        if (distanceToMouse > threshold) {
          return;
        }

        const dampening = 1 / distanceToMouse;

        Matter.Body.applyForce(particle, particle.position, {
          x: maxXEffect * dampening,
          y: maxYEffect * dampening,
        });
      });
    }, 80);

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [engine]);
};

function ConfettiGeyser({
  position,
  enableCollisions,
  airFriction,
  velocity,
  angularVelocity,
  angle,
  spread,
  volatility,
  concentration,
  duration = Infinity,
}: Props) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  const [engine] = usePhysicsEngine(canvasRef);

  useGeneratedParticles({
    engine,
    position,
    enableCollisions,
    airFriction,
    velocity,
    angularVelocity,
    angle,
    spread,
    volatility,
    concentration,
    duration,
  });

  useParticleCleanup(engine);

  useMouseWind(engine);

  const windowDimensions = useWindowDimensions();

  return (
    <Wrapper>
      <canvas
        ref={canvasRef}
        width={windowDimensions.width}
        height={windowDimensions.height}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  pointer-events: none;
`;

export default ConfettiGeyser;
